import { ShieldCheck, BadgeCheck, Phone, AtSign, Award } from "lucide-react";

/**
 * VerificationBadges — compact row of trust badges with tooltips.
 *
 * Props:
 *   - badges: object from /users/{id}/feedback-summary → { identity_verified, email_verified, phone_verified, trusted_trader }
 *   - size?: "sm" | "md" (default md)
 */
const BADGE_DEFS = [
  { key: "identity_verified", label: "Identity verified", icon: BadgeCheck, color: "#0654BA" },
  { key: "trusted_trader",    label: "Trusted trader",    icon: Award,      color: "#F5AF02" },
  { key: "phone_verified",    label: "Phone verified",    icon: Phone,      color: "#16A34A" },
  { key: "email_verified",    label: "Email verified",    icon: AtSign,     color: "#64748B" },
];

export default function VerificationBadges({ badges, size = "md" }) {
  if (!badges) return null;
  const active = BADGE_DEFS.filter(b => badges[b.key]);
  if (active.length === 0) return null;
  const sz = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <div className="flex items-center flex-wrap gap-1.5" data-testid="verification-badges">
      {active.map(b => {
        const Icon = b.icon;
        return (
          <span
            key={b.key}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border border-slate-200 bg-white text-slate-700"
            title={b.label}
            data-testid={`badge-${b.key}`}
          >
            <Icon className={sz} style={{ color: b.color }} strokeWidth={2.5} />
            {size === "md" && <span className="hidden sm:inline">{b.label.replace(" verified", "").replace(" trader", "")}</span>}
          </span>
        );
      })}
    </div>
  );
}

export function ReputationLine({ summary }) {
  if (!summary) return null;
  const { counts, positive_pct, total_feedback, transactions_completed } = summary;
  if (total_feedback === 0 && transactions_completed === 0) {
    return <p className="text-xs text-slate-500">New trader — no reviews yet</p>;
  }
  return (
    <div className="space-y-1.5">
      {total_feedback > 0 && (
        <p className="text-[13px] font-semibold text-emerald-700 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
          {positive_pct}% positive feedback
          <span className="text-slate-400 font-normal">({total_feedback} {total_feedback === 1 ? "review" : "reviews"})</span>
        </p>
      )}
      {transactions_completed > 0 && (
        <p className="text-[11px] text-slate-500">
          {transactions_completed} successful {transactions_completed === 1 ? "transaction" : "transactions"}
        </p>
      )}
      {total_feedback > 0 && (
        <div className="flex items-center gap-3 text-[10px] font-semibold pt-1" data-testid="feedback-breakdown">
          <span className="text-emerald-700">+ {counts.positive}</span>
          <span className="text-slate-500">~ {counts.neutral}</span>
          <span className="text-rose-700">– {counts.negative}</span>
        </div>
      )}
    </div>
  );
}
